import { useState } from 'react';
import { Flag } from '@weave/schema-gen-ts/dist/schemas/feature-flags/feature_flags_types.pb';
import { useQueryClient } from 'react-query';
import { useLocationService } from '@frontend/wam-location-service';
import { theme } from '@frontend/theme';
import {
  Accordion,
  Modal,
  ModalControlModalProps,
  Slider,
  SwitchField,
  Text,
  TextareaField,
  useForm,
} from '@frontend/design-system';
import { useBatchSetForLocationsMutation, useSetFlagAllMutation, useSetFlagPercentageMutation } from '../queries';

type Props = {
  modalProps: ModalControlModalProps;
  flag?: Flag;
};

export const BatchSetModal = ({ modalProps, flag }: Props) => {
  const { currentLocationId: locationId } = useLocationService();
  const queryClient = useQueryClient();
  const [currentAccordion, setCurrentAccordion] = useState<string>('list');
  const [currentPercentage, setCurrentPercentage] = useState<number>(0);
  const { mutate: batchSetForLocations } = useBatchSetForLocationsMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries([locationId, 'location-feature-flags']);
        queryClient.invalidateQueries(['feature-flags']);
        queryClient.invalidateQueries(['locations-with-flag']);
        modalProps.onClose();
      },
    },
  });

  const { mutate: setFlagAll } = useSetFlagAllMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries([locationId, 'location-feature-flags']);
        queryClient.invalidateQueries(['feature-flags']);
        queryClient.invalidateQueries(['locations-with-flag']);
        modalProps.onClose();
      },
    },
  });

  const { mutate: setFlagPercentage } = useSetFlagPercentageMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries([locationId, 'location-feature-flags']);
        queryClient.invalidateQueries(['feature-flags']);
        queryClient.invalidateQueries(['locations-with-flag']);
        modalProps.onClose();
      },
    },
  });

  const { getFieldProps, values } = useForm({
    fields: {
      list: { type: 'text', placeholder: 'Ex. 00000000-0000-0000-0000-00000000' },
      enabled: { type: 'switch', value: false },
    },
  });

  const createLocationIdsMap = (input: string) => input.split('\n').filter((line) => line.trim() !== '');

  const handleSet = () => {
    switch (currentAccordion) {
      case 'list':
        batchSetForLocations({
          flagName: flag?.name ?? '',
          locationIds: createLocationIdsMap(values.list ?? ''),
          value: values.enabled,
        });
        return;
      case 'percentage':
        setFlagPercentage({
          flagName: flag?.name ?? '',
          percentage: currentPercentage,
          value: values.enabled,
        });
        return;
      case 'all':
        setFlagAll({
          value: values.enabled,
          flagName: flag?.name ?? '',
        });
        return;
      case 'default':
        setFlagAll({
          value: flag?.defaultValue,
          flagName: flag?.name ?? '',
        });
        return;
      default:
        modalProps.onClose();
    }
  };

  return (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>
        <Text size='inherit'>Batch Set</Text>
        <Text>{flag?.name}</Text>
      </Modal.Header>
      <Modal.Body css={{ paddingTop: theme.spacing(2) }}>
        <Accordion
          onChange={(current) => {
            setCurrentAccordion(current ?? '');
          }}
        >
          <Accordion.Item value='list'>
            <Accordion.Header title=' Set Custom List' />
            <Accordion.Body>
              <TextareaField {...getFieldProps('list')} label='Place 1 location ID per line' />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='percentage'>
            <Accordion.Header title='Set Custom Percentage' />
            <Accordion.Body
              css={{
                padding: theme.spacing(5, 2),
              }}
            >
              <Slider min={0} max={100} step={1} onChange={(value) => setCurrentPercentage(value)} />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='all'>
            <Accordion.Header title='Update All Values' />
            <Accordion.Body>
              <Text>Set all Weave locations in one go.</Text>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='default'>
            <Accordion.Header title='Set Default Value' />
            <Accordion.Body>
              <Text>Set the default value for any new locations added in the future.</Text>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <SwitchField {...getFieldProps('enabled')} label={values.enabled ? 'Enabled' : 'Disabled'} />
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: 'Set',
          onClick: handleSet,
        }}
        secondary={{
          label: 'Cancel',
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
